import type { ReactNode } from 'react';

import { AccountMenu } from '@/components/AccountMenu/AccountMenu';
import { LogoMark } from '@/components/LogoMark/LogoMark';

import styles from './PageShell.module.css';

export interface PageShellProps {
  /** Shown top-right when someone is signed in: their name, or email. */
  signedInAs?: string | null;
  signedInEmail?: string | null;
  onSettings?: () => void;
  onApiKeys?: () => void;
  onSignOut?: () => void;
  /** The section links, rendered inside the shell's one `nav` landmark. The
   *  links themselves are the router's business, so they arrive as a slot. */
  nav?: ReactNode;
  /** Which deployment this build talks to; drawn as a badge beside the brand. */
  environment?: string | null;
  /** Drop the header and let the page fill the window, as sign-in does. */
  bare?: boolean;
  /** Draw the dot texture behind the page; off where the content has its own. */
  textured?: boolean;
  children: ReactNode;
}

/**
 * The one page frame. It renders the app's landmarks (`header`/`main`), so no
 * feature below it should add another — a second banner is a duplicate
 * landmark and a screen reader reads it as a second page header.
 */
export function PageShell({
  signedInAs,
  signedInEmail,
  onSettings,
  onApiKeys,
  onSignOut,
  nav,
  environment,
  bare = false,
  textured = true,
  children,
}: PageShellProps) {
  if (bare) {
    return <main className={styles.bare}>{children}</main>;
  }

  return (
    <div className={`${styles.shell} ${textured ? styles.textured : ''}`}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <span className={styles.brand}>
            <span className={styles.mark}>
              <LogoMark size={22} />
            </span>
            LendWell <span className={styles.brandMuted}>developer portal</span>
            {environment ? <span className={styles.env}>{environment}</span> : null}
          </span>
          {nav ? (
            <nav aria-label="Sections" className={styles.nav}>
              {nav}
            </nav>
          ) : null}
          {signedInAs ? (
            <AccountMenu
              name={signedInAs}
              email={signedInEmail}
              onSettings={onSettings}
              onApiKeys={onApiKeys}
              onSignOut={onSignOut}
            />
          ) : null}
        </div>
      </header>
      <main className={styles.main}>{children}</main>
    </div>
  );
}
